import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import theme from '../../../../styled-components/dark';
import { SportIds, type SportId } from '../../../../types/sports';
import { ObjectValuesUnionType } from '../../../../utils/typing';
import {
  LineUpSoccerGoalIconSvg,
  LineUpSoccerOwnGoalIconSvg,
  LineUpRugbyGoalIconSvg,
  LineUpIceHockeyGoalIconSvg,
} from '../../assets/svg';

const Sizes = {
  RUGBY: 'RUGBY',
  SOCCER: 'SOCCER',
  ICEHOCKEY: 'ICEHOCKEY',
};

type SizeType = ObjectValuesUnionType<typeof Sizes>;

type SIZESType = {
  justifyContent: string;
  width: string;
  right: string;
  bottom: string;
  multiplicator: {
    padding: string;
    transform: string;
  };
};

const SIZES: Record<SizeType, SIZESType> = {
  SOCCER: {
    justifyContent: 'flex-start',
    width: 'unset',
    right: '0.1em',
    bottom: '0em',
    multiplicator: {
      padding: '10% 0.3em 0.1em 0.3em',
      transform: 'unset',
    },
  },
  RUGBY: {
    justifyContent: 'center',
    width: '100%',
    right: '0.3em',
    bottom: '-0.3em',
    multiplicator: {
      padding: '10% 0.3em 0em 0.3em',
      transform: 'translateY(0.3em)',
    },
  },
  ICEHOCKEY: {
    justifyContent: 'flex-start',
    width: '100%',
    right: '0em',
    bottom: '0em',
    multiplicator: {
      padding: '10% 0.3em 0em 0.3em',
      transform: 'translateY(-0.3em)',
    },
  },
};

type WrapperType = {
  $size: SizeType;
};

const Wrapper = styled.div<WrapperType>`
  position: absolute;
  transform: translateX(100%);

  display: flex;
  align-items: end;
  font-size: 90%;

  ${({ $size }) => css`
    width: ${SIZES[$size].width};
    justify-content: ${SIZES[$size].justifyContent};
    right: ${SIZES[$size].right};
    bottom: ${SIZES[$size].bottom};
  `};
`;

const hasJustChangedAnimation = keyframes`
  50% {
    transform: scale(2);
  }
`;

type hasJustChangedSharedStyleType = {
  $hasJustChanged: boolean;
};

const hasJustChangedSharedStyle = css<hasJustChangedSharedStyleType>`
  ${({ $hasJustChanged }) => css`
    ${$hasJustChanged
      ? css`
          animation: ${hasJustChangedAnimation} 1000ms ease-in-out;
        `
      : undefined}
  `}
`;

type GoalsBlockType = {
  $superposed: boolean;
  $isOwnGoal?: boolean;
} & hasJustChangedSharedStyleType;

const GoalsBlock = styled.div<GoalsBlockType>`
  position: relative;

  display: flex;
  flex-direction: column-reverse;
  align-items: center;

  ${({ $superposed, $isOwnGoal }) =>
    $superposed
      ? css`
          transform: ${$isOwnGoal ? 'translateX(-0.4em)' : 'translateX(-65%)'};
        `
      : undefined}

  ${hasJustChangedSharedStyle}
`;

type GoalMultiplicatorType = {
  $isOwnGoal?: boolean;
  $size: SizeType;
};

const GoalMultiplicator = styled.div<GoalMultiplicatorType>`
  line-height: 1em;
  font-weight: bold;
  font-size: 120%;

  ${({ $isOwnGoal, $size }) => css`
    color: ${$isOwnGoal ? theme.LineUpPlayerGoalMultiplicator.ownGoalColor : theme.LineUpPlayerGoalMultiplicator.color};
    padding: ${SIZES[$size].multiplicator.padding};
    transform: ${SIZES[$size].multiplicator.transform};
  `}
`;

const sharedSoccerGoalIconsStyle = css`
  width: 1.6em;
  height: 1.6em;
`;

const sharedRugbyGoalIconsStyle = css`
  width: 2.3em;
  height: 2.3em;
`;

const sharedIceHockeyGoalIconsStyle = css`
  width: 1.2em;
  height: 1.2em;
  margin: 0 0.4em;
`;

const StyledLineUpSoccerGoalIconSvg = styled(LineUpSoccerGoalIconSvg)`
  ${sharedSoccerGoalIconsStyle}
`;

const StyledLineUpSoccerOwnGoalIconSvg = styled(LineUpSoccerOwnGoalIconSvg)`
  ${sharedSoccerGoalIconsStyle}
`;

const StyledLineUpIceHockeyGoalIconSvg = styled(LineUpIceHockeyGoalIconSvg)`
  ${sharedIceHockeyGoalIconsStyle}
`;

const StyledLineUpRugbyGoalIconSvg = styled(LineUpRugbyGoalIconSvg)`
  ${sharedRugbyGoalIconsStyle}
`;

type LineUpGoalsInfoProps = {
  sportId?: SportId;
  nbGoals?: number;
  nbOwnGoals?: number;
  hasNbGoalsJustChanged?: boolean;
  hasNbOwnGoalsJustChanged?: boolean;
};

const LineUpGoalsInfo = ({
  sportId,
  nbGoals = 0,
  nbOwnGoals = 0,
  hasNbGoalsJustChanged = false,
  hasNbOwnGoalsJustChanged = false,
}: LineUpGoalsInfoProps) => {
  const goalIcon = useMemo(() => {
    switch (sportId) {
      case SportIds.RUGBY:
      case SportIds.RUGBY_LEAGUE:
        return <StyledLineUpRugbyGoalIconSvg />;
      case SportIds.ICEHOCKEY:
        return <StyledLineUpIceHockeyGoalIconSvg />;
      default:
        return <StyledLineUpSoccerGoalIconSvg />;
    }
  }, [sportId]);

  const displayOwnGoal = useMemo(() => nbOwnGoals !== 0 && sportId === SportIds.SOCCER, [nbOwnGoals, sportId]);

  const displayGoalMultiplication = useMemo(
    () => (nbGoals && nbGoals > 1) || (nbOwnGoals && nbOwnGoals > 1 && sportId === SportIds.SOCCER),
    [nbGoals, nbOwnGoals, sportId],
  );

  const indicatorSize = useMemo(() => {
    switch (sportId) {
      case SportIds.RUGBY:
      case SportIds.RUGBY_LEAGUE:
        return Sizes.RUGBY;
      case SportIds.ICEHOCKEY:
        return Sizes.ICEHOCKEY;
      case SportIds.SOCCER:
      default:
        return Sizes.SOCCER;
    }
  }, [sportId]);

  return (
    <Wrapper $size={indicatorSize}>
      {displayOwnGoal ? (
        <GoalsBlock $superposed={displayOwnGoal} $isOwnGoal={true} $hasJustChanged={hasNbOwnGoalsJustChanged}>
          <StyledLineUpSoccerOwnGoalIconSvg />
          {displayGoalMultiplication ? (
            <GoalMultiplicator $isOwnGoal $size={indicatorSize}>
              {nbOwnGoals}
            </GoalMultiplicator>
          ) : null}
        </GoalsBlock>
      ) : null}
      {nbGoals ? (
        <GoalsBlock $superposed={displayOwnGoal} $hasJustChanged={hasNbGoalsJustChanged}>
          {goalIcon}
          {displayGoalMultiplication ? <GoalMultiplicator $size={indicatorSize}>{nbGoals}</GoalMultiplicator> : null}
        </GoalsBlock>
      ) : null}
    </Wrapper>
  );
};

export default LineUpGoalsInfo;
